import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IReservaRecursoData,
  IReservaHorarioData,
  IReservaBloqueoData,
  IReservaData,
  IDisponibilidadData,
} from '@core/interfaces/entidades/reserva.entidad';
import {
  ICrearRecursoReq,
  IEditarRecursoReq,
  ICrearHorarioReq,
  ICrearBloqueoReq,
  ICrearReservaReq,
  IEditarReservaReq,
  ICambiarEstadoReservaReq,
  IFiltrarReservasReq,
} from '@core/interfaces/request-response/reserva.dto.interface';
import { IPaginar } from '@core/interfaces/paginacion/paginacion.interface';
import { environment } from '@env/environment';
import { BaseHttpService } from '@shared/services/base-http.service';

@Injectable({ providedIn: 'root' })
export class ReservaApiService extends BaseHttpService {
  private readonly baseUrl = `${environment.apiReservasUrl}${environment.apiPreFix}/reservas`;

  // ── Recursos ──

  listarRecursos() {
    return this.withLoader(
      this.httpClient.get<IReservaRecursoData[]>(`${this.baseUrl}/recurso`),
    );
  }

  obtenerRecurso(id: number) {
    return this.httpClient.get<IReservaRecursoData>(`${this.baseUrl}/recurso/${id}`);
  }

  crearRecurso(dto: ICrearRecursoReq) {
    return this.withLoader(
      this.httpClient.post<IReservaRecursoData>(`${this.baseUrl}/recurso`, dto),
    );
  }

  editarRecurso(id: number, dto: IEditarRecursoReq) {
    return this.withLoader(
      this.httpClient.put<IReservaRecursoData>(`${this.baseUrl}/recurso/${id}`, dto),
    );
  }

  eliminarRecurso(id: number) {
    return this.withLoader(
      this.httpClient.patch<void>(`${this.baseUrl}/recurso/${id}/eliminar`, {}),
    );
  }

  // ── Horarios ──

  listarHorarios(recursoId: number) {
    return this.httpClient.get<IReservaHorarioData[]>(`${this.baseUrl}/recurso/${recursoId}/horario`);
  }

  crearHorario(recursoId: number, dto: ICrearHorarioReq) {
    return this.withLoader(
      this.httpClient.post<IReservaHorarioData>(`${this.baseUrl}/recurso/${recursoId}/horario`, dto),
    );
  }

  reemplazarHorarios(recursoId: number, dtos: ICrearHorarioReq[]) {
    return this.withLoader(
      this.httpClient.put<IReservaHorarioData[]>(`${this.baseUrl}/recurso/${recursoId}/horario`, dtos),
    );
  }

  eliminarHorario(id: number) {
    return this.withLoader(
      this.httpClient.patch<void>(`${this.baseUrl}/horario/${id}/eliminar`, {}),
    );
  }

  // ── Bloqueos ──

  listarBloqueos(recursoId: number) {
    return this.httpClient.get<IReservaBloqueoData[]>(`${this.baseUrl}/recurso/${recursoId}/bloqueo`);
  }

  crearBloqueo(recursoId: number, dto: ICrearBloqueoReq) {
    return this.withLoader(
      this.httpClient.post<IReservaBloqueoData>(`${this.baseUrl}/recurso/${recursoId}/bloqueo`, dto),
    );
  }

  eliminarBloqueo(id: number) {
    return this.withLoader(
      this.httpClient.patch<void>(`${this.baseUrl}/bloqueo/${id}/eliminar`, {}),
    );
  }

  // ── Reservas ──

  listarReservas(filtros: IFiltrarReservasReq) {
    const params = new HttpParams({ fromObject: { ...filtros } });
    return this.withLoader(
      this.httpClient.get<IPaginar<IReservaData>>(`${this.baseUrl}`, { params }),
    );
  }

  obtenerReserva(id: number) {
    return this.httpClient.get<IReservaData>(`${this.baseUrl}/${id}`);
  }

  crearReserva(dto: ICrearReservaReq) {
    return this.withLoader(
      this.httpClient.post<IReservaData>(`${this.baseUrl}`, dto),
    );
  }

  editarReserva(id: number, dto: IEditarReservaReq) {
    return this.withLoader(
      this.httpClient.put<IReservaData>(`${this.baseUrl}/${id}`, dto),
    );
  }

  cambiarEstado(id: number, dto: ICambiarEstadoReservaReq) {
    return this.withLoader(
      this.httpClient.patch<IReservaData>(`${this.baseUrl}/${id}/estado`, dto),
    );
  }

  // ── Agenda / Disponibilidad ──

  obtenerAgenda(recursoId: number, fecha: string) {
    const params = new HttpParams().set('fecha', fecha);
    return this.httpClient.get<IReservaData[]>(`${this.baseUrl}/agenda/${recursoId}`, { params });
  }

  consultarDisponibilidad(recursoId: number, fecha: string) {
    const params = new HttpParams().set('recurso_id', recursoId).set('fecha', fecha);
    return this.httpClient.get<IDisponibilidadData>(`${this.baseUrl}/disponibilidad/consultar`, { params });
  }
}
